import React, { useEffect, useState, useCallback } from "react";
import { client, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Lock, Trash2, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const PrayerRequests = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/prayer-requests");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await client.patch(`/admin/prayer-requests/${id}`, { status });
    setItems((s) => s.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this prayer request?")) return;
    await client.delete(`/admin/prayer-requests/${id}`);
    setItems((s) => s.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2">
        <Lock className="h-5 w-5 text-brand-orange" />
        <h1 className="font-display text-2xl font-bold text-navy">Prayer Requests</h1>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">Confidential — visible only to authorized staff.</p>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div> : (
        <div className="space-y-4" data-testid="prayer-list">
          {items.length === 0 && <p className="rounded-sm border border-dashed border-border bg-white py-16 text-center text-muted-foreground">No prayer requests.</p>}
          {items.map((p) => (
            <div key={p.id} className="rounded-sm border border-border bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-display font-semibold text-navy">{p.name}</span>
                    <span className="rounded-sm bg-secondary px-2 py-0.5 text-xs capitalize text-navy">{p.category}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{p.message}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    {p.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {p.email}</span>}
                    {p.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</span>}
                    <span>{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={p.status || "new"} onValueChange={(v) => setStatus(p.id, v)}>
                    <SelectTrigger className="w-32 rounded-sm" data-testid={`prayer-status-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="praying">Praying</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrayerRequests;
